import { DashBoardEventDto } from '../../types/DashBoardEventDto.tsx'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks.tsx'
import { protectedResources } from '../../global/config.tsx'
import { useOidcAccessToken } from '@axa-fr/react-oidc'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { clearEventList, clearSelectedEvent, fetchEventsFromApiData, setSelectedYear } from '../../redux/eventSlice.tsx'
import styles from '../../styles/components/events.module.scss'
import { useNavigate } from 'react-router-dom'
import { createSpinner, hideSpinner, showSpinner } from '@syncfusion/ej2-react-popups'
import { ChangeEventArgs, DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import { MenuHeaderBar } from '../../components/global/MenuHeaderBar.tsx'
import { GridComponent, ColumnsDirective, ColumnDirective, RowSelectEventArgs, Inject, Filter } from '@syncfusion/ej2-react-grids'
import PeopleIcon from '@mui/icons-material/People'
import CustomCard from '../../components/CustomCard.tsx'
import { ButtonComponent } from '@syncfusion/ej2-react-buttons'

export const EventList = () => {
  const eventData = useAppSelector((state) => state.event)
  const loading = useAppSelector((state) => state.event.loading)
  const selectedYearFromStore = useAppSelector((state) => state.event.selectedYear)
  const selectedDocentId = useAppSelector((state) => state.app.selectedDocIdx)
  const { accessToken } = useOidcAccessToken()

  const currentYear: number = dayjs().year()
  const yearsToFilter: number[] = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear, currentYear + 1]
  const statusFilterDefaultAll = 'Alle'

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [firstLastDayOfYear, setFirstLastDayOfYear] = useState({ first: dayjs().year(selectedYearFromStore).startOf('year').format('DD.MM.YYYY'), last: dayjs().year(selectedYearFromStore).endOf('year').format('DD.MM.YYYY') })
  const [activeFilterBtnIdx, setActiveFilterBtnIdx] = useState(selectedYearFromStore)
  const [uniqueStatuses, setUniqueStatuses] = useState<string[]>([])
  const [selectedStatus, setSelectedStatus] = useState<string>(statusFilterDefaultAll) // Default Filter Alle
  const eventListGrid = useRef<GridComponent>(null)

  const filterStatusDdl = useRef<DropDownListComponent>(null)

  // Setzte ersten und letzten Tag des Jahres für Grid-Filterung, in Abhängigkeit von Redux variable
  useEffect(() => {
    setFirstLastDayOfYear({
      first: dayjs().year(selectedYearFromStore).startOf('year').format('DD.MM.YYYY'),
      last: dayjs().year(selectedYearFromStore).endOf('year').format('DD.MM.YYYY'),
    })
    setActiveFilterBtnIdx(selectedYearFromStore)
    if (accessToken && selectedDocentId) {
      const url = `${protectedResources.apiList.readDashboard}/${selectedDocentId}/${firstLastDayOfYear.first}/${firstLastDayOfYear.last}`
      dispatch(clearEventList())
      dispatch(fetchEventsFromApiData({ url: url, token: accessToken })).then(() => {
        eventListGrid.current?.refresh()
      })
    }
  }, [selectedYearFromStore, selectedDocentId])


  useEffect(() => {
    // Unique Werte für Status-Filter setzen
    if (eventData && eventData.eventDtos) {
      const statuses = eventData.eventDtos.map((event) => event.eventStatus)
      const uniqueStatusesArray = Array.from(new Set(statuses))
      setUniqueStatuses(uniqueStatusesArray)
    } else {
      setUniqueStatuses([])
    }
  }, [eventData])


  useEffect(() => {
    if (selectedStatus === statusFilterDefaultAll) {
      eventListGrid?.current?.clearFiltering()
    } else {
      if (eventListGrid)
        eventListGrid?.current?.filterByColumn('eventStatus', 'startsWith', selectedStatus)
    }
  }, [selectedStatus, eventData])

  const handleStatusChange = (args: ChangeEventArgs) => {
    setSelectedStatus(String(args.value))
  }

  // Filter Events nach Jahren, resette Status-Filter auf "Alle" bei Jahrswechsel
  const handleFilterYears = (year: number) => {
    setSelectedStatus('Alle')

    // Hole ersten und letzten Tag des gewählten Jahres und setze als API params
    setFirstLastDayOfYear({ first: dayjs().year(year).startOf('year').format('DD.MM.YYYY'), last: dayjs().year(year).endOf('year').format('DD.MM.YYYY') })

    // // Setze aktiven Filter Button
    setActiveFilterBtnIdx(year)

    // // Setze aktives Jahr im redux store
    dispatch(setSelectedYear(year))
  }

  useEffect(() => {
    dispatch(clearSelectedEvent())
    const html = document.getElementsByClassName('nw-app')[0]
    if (html instanceof HTMLElement) {
      createSpinner({
        target: html,
      })
    }
  }, [dispatch])

  useEffect(() => {
    const html = document.getElementsByClassName('nw-app')[0]
    if (loading === 'succeeded') {
      if (html instanceof HTMLElement) {
        hideSpinner(html)
      }
    } else if (loading !== 'pending') {
      if (html instanceof HTMLElement) {
        hideSpinner(html)
      }
    } else if (loading === 'pending') {
      if (html instanceof HTMLElement) {
        showSpinner(html)
      }
    }
  }, [loading])

  // Gehe zu Detailseite
  const handleRowClick = (args: RowSelectEventArgs) => {
    const eventData = args.data as DashBoardEventDto

    navigate(`/seminare/${eventData.eventId}`)
  }

  const titelTemplate = (props: Partial<DashBoardEventDto>) => {
    let colorClass
    if (props.eventStatus) {
      switch (props.eventStatus) {
        case 'Durchgeführt':
          colorClass = 'blue'
          break
        case 'Aktiv':
          colorClass = 'green'
          break
        case 'Geplant':
          colorClass = 'yellow'
          break
        case 'Wahrscheinlich':
          colorClass = 'lightGreen'
          break
        default:
          // Default/Fallback Farbe
          colorClass = 'grey'
          break
      }

      return (
        <div className={`${styles.status} ${styles[colorClass]}`}>
          {props.eventTitle}
          {props.eventIsInhouse && <span className={styles.badge}>InHouse</span>}
        </div>
      )
    }
  }

  const tlnTemplate = (props: Partial<DashBoardEventDto>) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
        {props.eventActParticipant}
        <PeopleIcon sx={{ fontSize: '18px' }} />
      </div>
    )
  }


  return (
    <>
      <MenuHeaderBar headerContent={'Meine Veranstaltungen'}></MenuHeaderBar>
      <div className={`nw-content ${styles.eventList}`} style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Nur sichtbar wenn User nicht Rolle intern hat, oder wenn eine Adr No gesucht wurde */}
        <>
          <CustomCard noBottomMargin={true} style={{ zIndex: 1, boxShadow: 'none' }}>
            <div className={`${styles.filterBar} ${selectedDocentId === 0 ? styles.disabled : ''}`}>
              <div>
                {yearsToFilter.map(function(item) {
                  return (
                    <ButtonComponent key={item} onClick={() => handleFilterYears(item)} className={`e-link e-btn ${styles.filterBtn} ${item == activeFilterBtnIdx ? styles.active : 'inactive'}`}>
                      {item}
                    </ButtonComponent>
                  )
                })}
              </div>
              <div className={styles.statusFilter}>
                <p className={styles.filterLabel}>Status: </p>
                <DropDownListComponent ref={filterStatusDdl} width={150} dataSource={[statusFilterDefaultAll].concat(uniqueStatuses)} value={selectedStatus} onChange={handleStatusChange} />
              </div>
            </div>
          </CustomCard>
          <CustomCard noBottomMargin={true} style={{ height: '100%' }}>
            <GridComponent ref={eventListGrid} dataSource={eventData.eventDtos} rowHeight={40} allowFiltering={true} rowSelected={handleRowClick} height={'100%'}>
              <ColumnsDirective>
                <ColumnDirective field="eventId" headerText="Id" isPrimaryKey visible={false} />
                <ColumnDirective field="eventTitle" headerText="Veranstaltung" template={titelTemplate} width={'70%'} />
                <ColumnDirective field="eventFirstDayStart" headerText="Datum von" type="Date" width="120" format="dd.MM.yyyy" />
                <ColumnDirective field="eventLastDayEnd" headerText="Datum bis" type="Date" width="120" format="dd.MM.yyyy" />
                <ColumnDirective field="eventStatus" headerText="Status" width="130"></ColumnDirective>
                <ColumnDirective field="eventActParticipant" headerText="Teilnehmer" width="120" template={tlnTemplate} textAlign="Center" />
              </ColumnsDirective>
              <Inject services={[Filter]} />
            </GridComponent>
          </CustomCard>
        </>
      </div>
    </>
  )
}
